import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth, ROLE_LABELS } from '../contexts/MemberAuthContext';
import FeedbackButton from '../components/FeedbackButton';
import Icon, { CATEGORY_TONES } from '../components/Icon';

// Velké vstupy podle kontextu. Prázdné (dle role) se skryjí.
const SECTIONS = [
  { key: 'mu', title: 'Mimořádné události', subtitle: 'Aktivní zásah – MU, konvoje, předání', icon: 'alert', tone: 'udalosti' },
  { key: 'spolek', title: 'Můj spolek', subtitle: 'Sklad, vozidla, členové „v době míru“', icon: 'home', tone: 'material' },
  { key: 'sprava', title: 'Správa systému', subtitle: 'Katalog, spolky, audit, zpětná vazba', icon: 'gear', tone: 'sprava' },
];

// Dlaždice modulů; `section` určuje zařazení, `cat` jen barevný tón ikony.
const MODULES = [
  { key: 'mu', section: 'mu', cat: 'udalosti', title: 'Mimořádné události', icon: 'alert', path: '/mu',
    desc: 'Správa MU, zóny, checkpointy a přehled materiálu', roles: ['superadmin', 'ops'] },
  { key: 'checkin', section: 'mu', cat: 'personal', title: 'Příchod / odchod', icon: 'pin', path: '/checkin/remote',
    desc: 'Přihlášení a odhlášení ze zóny (sken nebo ruční výběr)', roles: ['superadmin', 'ops', 'chapter_rep', 'member'] },
  { key: 'mapa-mu', section: 'mu', cat: 'udalosti', title: 'Mapa MU', icon: 'map', path: '/mu-mapa',
    desc: 'Mapa zón, checkpointů a bodů zájmu', roles: ['superadmin', 'ops', 'chapter_rep', 'member'] },
  { key: 'cesty', section: 'mu', cat: 'logistika', title: 'Přehled cest', icon: 'truck', path: '/cesty',
    desc: 'Konvoje a nakládka materiálu na MU', roles: ['superadmin', 'ops', 'chapter_rep'] },
  { key: 'moje-cesty', section: 'mu', cat: 'logistika', title: 'Moje cesty', icon: 'compass', path: '/moje-cesty',
    desc: 'Cesty, kde jsem řidičem – nakládka a předání', roles: ['superadmin', 'ops', 'chapter_rep', 'member'] },

  { key: 'sklad', section: 'spolek', cat: 'material', title: 'Sklad spolku', icon: 'box', path: '/sklad',
    desc: 'Materiál vašeho oblastního spolku', roles: ['superadmin', 'ops', 'chapter_rep', 'member'] },
  { key: 'euroboxy', section: 'spolek', cat: 'material', title: 'Euroboxy', icon: 'crates', path: '/euroboxy',
    desc: 'Předpřipravené boxy s obsahem a evidenčním číslem', roles: ['superadmin', 'ops', 'chapter_rep'] },
  { key: 'vozidla', section: 'spolek', cat: 'logistika', title: 'Vozidla', icon: 'van', path: '/vozidla',
    desc: 'Vozový park a vleky oblastního spolku', roles: ['superadmin', 'ops', 'chapter_rep'] },
  { key: 'odpisy', section: 'spolek', cat: 'material', title: 'Odpisy', icon: 'trendDown', path: '/odpisy',
    desc: 'Spotřebovaný a odepsaný materiál + export', roles: ['superadmin', 'ops', 'chapter_rep'] },
  { key: 'clenove', section: 'spolek', cat: 'personal', title: 'Členové', icon: 'users', path: '/clenove',
    desc: 'Přehled členů, jejich schopnosti, role a spolky', roles: ['superadmin', 'ops', 'chapter_rep'] },

  { key: 'katalog', section: 'sprava', cat: 'material', title: 'Katalog materiálu', icon: 'grid', path: '/katalog',
    desc: 'Kategorie, podkategorie a typy položek', roles: ['superadmin'] },
  { key: 'spolky', section: 'sprava', cat: 'sprava', title: 'Oblastní spolky', icon: 'building', path: '/spolky',
    desc: 'Spolky a připojovací kódy', roles: ['superadmin'] },
  { key: 'audit', section: 'sprava', cat: 'sprava', title: 'Audit log', icon: 'shield', path: '/audit',
    desc: 'Historie bezpečnostních akcí + export', roles: ['superadmin'] },
  { key: 'zpetna-vazba', section: 'sprava', cat: 'sprava', title: 'Zpětná vazba', icon: 'chat', path: '/zpetna-vazba',
    desc: 'Hlášení a náměty od uživatelů + export', roles: ['superadmin'] },
];

export default function ModuleLauncher() {
  const navigate = useNavigate();
  const { member, logout } = useAuth();
  const [section, setSection] = useState(null); // null = hub, jinak klíč sekce

  const visible = MODULES.filter((m) => m.roles.includes(member.role));
  const sections = SECTIONS.filter((s) => visible.some((m) => m.section === s.key));

  const handleLogout = async () => { await logout(); navigate('/login'); };
  const initials = `${member.first_name?.[0] || ''}${member.last_name?.[0] || ''}`.toUpperCase() || '?';

  const activeSection = SECTIONS.find((s) => s.key === section);
  const sectionMods = activeSection ? visible.filter((m) => m.section === section) : [];

  return (
    <div>
      {/* Hlavička */}
      <div className="header">
        <div className="brand">
          <img src="/logo.svg" width="38" height="38" alt="TEČČKa" />
          <div>
            <div className="header-title">TEČČKa</div>
            <div className="header-subtitle">Technická evidence ČČKa</div>
          </div>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 14 }}>
          <div className="identity" onClick={() => navigate('/nastaveni')} title="Nastavení"
            style={{ display: 'flex', alignItems: 'center', gap: 10, cursor: 'pointer' }}>
            <div style={{ textAlign: 'right', lineHeight: 1.25 }} className="hide-mobile">
              <div style={{ fontSize: '0.82rem', fontWeight: 600, color: 'var(--text)' }}>{member.first_name} {member.last_name}</div>
              <div style={{ fontSize: '0.7rem', color: 'var(--text-muted)' }}>{ROLE_LABELS[member.role]}</div>
            </div>
            <div className="avatar">{initials}</div>
          </div>
          <span style={{ width: 1, height: 28, background: 'var(--border)' }} className="hide-mobile" />
          <button className="icon-btn icon-btn-accent" onClick={() => navigate('/sken')} title="Skenovat QR">
            <Icon name="qr" size={15} />
            <span className="hide-mobile">Skenovat</span>
          </button>
          <FeedbackButton variant="header" />
          <button className="icon-btn" onClick={handleLogout}>
            <Icon name="logout" size={15} />
            <span className="hide-mobile">Odhlásit</span>
          </button>
        </div>
      </div>

      <div className="container-wide">
        {activeSection ? (
          /* ── Zaměřený pohled na sekci ── */
          <div>
            <button className="icon-btn" style={{ marginBottom: 18 }} onClick={() => setSection(null)}>
              <Icon name="arrowLeft" size={15} /> Zpět na rozcestník
            </button>
            <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 20 }}>
              <div style={{ width: 46, height: 46, borderRadius: 12, display: 'flex', alignItems: 'center', justifyContent: 'center', background: CATEGORY_TONES[activeSection.tone].bg, color: CATEGORY_TONES[activeSection.tone].fg, flex: 'none' }}>
                <Icon name={activeSection.icon} size={24} strokeWidth={1.7} />
              </div>
              <div>
                <h1 style={{ margin: 0 }}>{activeSection.title}</h1>
                <p className="text-muted" style={{ fontSize: '0.88rem', margin: 0 }}>{activeSection.subtitle}</p>
              </div>
            </div>
            <Tiles mods={sectionMods} navigate={navigate} />
          </div>
        ) : (
          /* ── Hub ── */
          <div>
            <div style={{ marginBottom: 24 }}>
              <h1 style={{ margin: '0 0 4px' }}>Vítejte, {member.first_name}</h1>
              <p className="text-muted" style={{ fontSize: '0.9rem', margin: 0 }}>Vyberte, s čím chcete pracovat.</p>
            </div>

            {/* Velké sekce */}
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))', gap: 16 }}>
              {sections.map((s) => {
                const t = CATEGORY_TONES[s.tone];
                const count = visible.filter((m) => m.section === s.key).length;
                return (
                  <div key={s.key} className="card card-clickable" style={{ margin: 0, padding: 22 }}
                    onClick={() => setSection(s.key)}>
                    <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between' }}>
                      <div style={{ width: 52, height: 52, borderRadius: 14, display: 'flex', alignItems: 'center', justifyContent: 'center', background: t.bg, color: t.fg, flex: 'none' }}>
                        <Icon name={s.icon} size={26} strokeWidth={1.7} />
                      </div>
                      <Icon name="chevronRight" size={20} color="#cbd5e1" strokeWidth={2} />
                    </div>
                    <h2 style={{ margin: '16px 0 4px' }}>{s.title}</h2>
                    <p className="text-muted" style={{ margin: 0, fontSize: '0.86rem', lineHeight: 1.45 }}>{s.subtitle}</p>
                    <div className="text-muted" style={{ marginTop: 12, fontSize: '0.78rem', fontWeight: 600 }}>{count} {count === 1 ? 'modul' : count < 5 ? 'moduly' : 'modulů'}</div>
                  </div>
                );
              })}
            </div>

            <div style={{ marginTop: 24 }}>
              <button className="icon-btn" onClick={() => navigate('/nastaveni')}>
                <Icon name="gear" size={16} />
                Nastavení
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

// Mřížka dlaždic modulů (barevný icon-chip dle `cat`).
function Tiles({ mods, navigate }) {
  if (mods.length === 0) return <p className="text-muted">Žádné moduly.</p>;
  return (
    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(248px, 1fr))', gap: 14 }}>
      {mods.map((m) => {
        const t = CATEGORY_TONES[m.cat];
        return (
          <div key={m.key} className="card card-clickable" style={{ margin: 0, padding: 18 }}
            onClick={() => navigate(m.path)}>
            <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between' }}>
              <div style={{ width: 42, height: 42, borderRadius: 10, display: 'flex', alignItems: 'center', justifyContent: 'center', background: t.bg, color: t.fg, flex: 'none' }}>
                <Icon name={m.icon} size={20} strokeWidth={1.7} />
              </div>
              <Icon name="chevronRight" size={18} color="#cbd5e1" strokeWidth={2} />
            </div>
            <h3 style={{ margin: '15px 0 5px', fontSize: '0.98rem' }}>{m.title}</h3>
            <p className="text-muted" style={{ margin: 0, fontSize: '0.82rem', lineHeight: 1.45 }}>{m.desc}</p>
          </div>
        );
      })}
    </div>
  );
}
